import React, { useState, useEffect } from 'react';
import SVGIcon from '../SVGIcon/SVGIcon';
import {
  FaWeightHanging,
  FaLeaf,
  FaBoxOpen,
  FaCheckCircle,
  FaDownload,
  FaArrowLeft,
  FaStar,
  FaStarHalfAlt
} from 'react-icons/fa';
import './ProductDetails.css';

export default function ProductDetails({
  product,
  onBack,
  selectedCurr = 'INR',
  convertPrice
}) {
  const [selectedImgIdx, setSelectedImgIdx] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Initialize selected variant
  useEffect(() => {
    if (product && product.variants && product.variants.length > 0) {
      setSelectedVariant(product.variants[0]);
    } else {
      setSelectedVariant(null);
    }
    setSelectedImgIdx(0);
  }, [product]);

  if (!product) {
    return (
      <div className="product-details-error container">
        <p>Product not found.</p>
        <button onClick={onBack} className="btn-back">
          <FaArrowLeft /> Back to Shop
        </button>
      </div>
    );
  }

  // Fallbacks
  const title = product.title || 'Organic Product';
  const category = product.category || product.category_name || 'Fertilizers';
  const description = product.description || '';
  const shortDescription = product.short_description || '';
  const brochureUrl = product.brochure_url || null;
  const isSuperNovel = title.toLowerCase().includes('super novel');
  const rating = isSuperNovel ? 4.5 : 4.0;

  const getProductReviewsCount = (titleStr) => {
    const t = (titleStr || '').toLowerCase();
    if (t.includes('super novel')) return 148;
    if (t.includes('super plus')) return 87;
    if (t.includes('super prime')) return 92;
    if (t.includes('super')) return 76;
    if (t.includes('prime')) return 64;
    if (t.includes('plus')) return 58;
    if (t.includes('enriched organic manure')) return 72;
    if (t.includes('organic manure')) return 50;
    if (t.includes('vermicompost')) return 98;
    return 45;
  };

  const reviewsCount = getProductReviewsCount(title);

  // Resolve images list
  const getProductDefaultImage = (titleStr) => {
    const t = (titleStr || '').toLowerCase();
    if (t.includes('super novel')) return '/products/bio-orbi-super-novel.png';
    if (t.includes('super plus')) return '/products/bio-orbi-super-plus.png';
    if (t.includes('super prime')) return '/products/bio-orbi-super-prime.png';
    if (t.includes('super')) return '/products/bio-orbi-super.png';
    if (t.includes('prime')) return '/products/bio-orbi-prime.png';
    if (t.includes('plus')) return '/products/bio-orbi-plus.png';
    if (t.includes('enriched organic manure')) return '/products/Enriched Organic Manure.png';
    if (t.includes('organic manure')) return '/products/Organic Manure.png';
    if (t.includes('vermicompost')) return '/products/Vermicompost.png';
    return '/products/bio-orbi-super-novel.png'; // Default fallback
  };

  const defaultImg = getProductDefaultImage(title);

  const rawImages = Array.isArray(product.images) && product.images.length > 0
    ? product.images
    : [product.image || defaultImg];

  const allImages = rawImages.map((img) => {
    if (img === '/products/bio-orbi-super-novel.png' && !title.toLowerCase().includes('super novel')) {
      return defaultImg;
    }
    return img;
  });

  // Price calculations based on selected variant
  const currentPrice = selectedVariant
    ? parseFloat(selectedVariant.price)
    : parseFloat(product.starting_price || product.price || 140);

  const comparePrice = selectedVariant
    ? (selectedVariant.compare_at_price ? parseFloat(selectedVariant.compare_at_price) : null)
    : (product.compare_at_price ? parseFloat(product.compare_at_price) : null);

  // Hardcoded premium static reviews matching the product nature
  const staticReviews = [
    {
      id: 1,
      name: "Ramesh Patel",
      rating: 5,
      date: "May 12, 2026",
      comment: "Highly recommended organic biofertilizer. I used this on my cotton crops this season and saw a noticeable improvement in root strength and leaf greenness. Very satisfied!"
    },
    {
      id: 2,
      name: "Siddharth Hegde",
      rating: 5,
      date: "June 04, 2026",
      comment: "Orbifert is doing a fantastic job with NPOP standard biofertilizers. Soil health has improved significantly, and water retention is much better."
    }
  ];

  return (
    <div className="product-details-page">
      <div className="container">
        {/* Back Button */}
        <button onClick={onBack} className="btn-back-link">
          <FaArrowLeft /> Back to Shop
        </button>

        <div className="details-layout">
          {/* LEFT: Product Images Carousel */}
          <div className="details-images-column">
            <div className="main-image-viewport">
              <img
                src={allImages[selectedImgIdx]}
                alt={title}
                onError={e => { e.target.src = defaultImg; }}
              />
            </div>
            {allImages.length > 1 && (
              <div className="thumbnails-strip">
                {allImages.map((img, idx) => (
                  <div
                    key={idx}
                    className={`thumbnail-item ${idx === selectedImgIdx ? 'active' : ''}`}
                    onClick={() => setSelectedImgIdx(idx)}
                  >
                    <img
                      src={img}
                      alt={`${title} thumbnail ${idx}`}
                      onError={e => { e.target.src = defaultImg; }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: Product Content & Actions */}
          <div className="details-content-column">
            <div className="details-category-badge">{category}</div>

            <h1 className="details-product-title">
              {product.title_first || title}
              {product.title_last && <span className="title-accent"> {product.title_last}</span>}
            </h1>

            {product.subname_first && (
              <div className="details-product-subname">
                <span className="subname-base">{product.subname_first}</span>
                {product.subname_last && <span className="subname-accent"> {product.subname_last}</span>}
              </div>
            )}

            {/* Ratings Row */}
            <div className="details-rating-row">
              <div className="stars-wrap">
                {Array.from({ length: 5 }).map((_, i) => {
                  if (i < Math.floor(rating)) {
                    return <FaStar key={i} className="star filled" />;
                  } else if (i === Math.floor(rating) && rating % 1 !== 0) {
                    return <FaStarHalfAlt key={i} className="star filled" />;
                  } else {
                    return <FaStar key={i} className="star" />;
                  }
                })}
              </div>
              <span className="rating-value">{rating.toFixed(1)} Rating</span>
              <span className="reviews-count">({reviewsCount} Customer Reviews)</span>
            </div>

            {/* Price Display */}
            {isSuperNovel && (
              <div className="details-price-row" style={{ display: 'flex', alignItems: 'baseline' }}>
                <span className="details-current-price">
                  {convertPrice ? convertPrice(currentPrice, selectedCurr) : `₹${currentPrice}`}
                </span>
                <span className="details-taxes-note" style={{ fontSize: '14px', color: '#666666', marginLeft: '10px', fontWeight: '500' }}>
                  (Inclusive of all taxes)
                </span>
              </div>
            )}

            {/* Short Description */}
            <p className="details-short-desc">{shortDescription}</p>

            {/* Variants Selector - Removed to eliminate ml sizes from layout */}

            {/* Main Purchasing Action */}
            <div className="details-action-block">
              {isSuperNovel ? (
                <a
                  href="https://gem.gov.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="details-buy-gem-btn"
                >
                  Buy on GeM Government Portal ➔
                </a>
              ) : (
                <div className="details-launch-soon-box">
                  <button className="details-launch-soon-btn" disabled>
                    Launching Soon
                  </button>
                  <span className="launch-soon-note">This product will be available for online retail and distribution soon.</span>
                </div>
              )}
            </div>

            {/* Brochure download */}
            {brochureUrl && (
              <div className="details-brochure-block">
                <a
                  href={brochureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-download-brochure"
                >
                  <FaDownload /> Download Product Brochure (PDF)
                </a>
              </div>
            )}

            {/* Organic Assurance Seals */}
            <div className="assurance-seals-grid">
              <div className="seal-item">
                <FaLeaf className="seal-icon" />
                <span>100% Organic certified</span>
              </div>
              <div className="seal-item">
                <FaCheckCircle className="seal-icon" />
                <span>Eco Friendly formulation</span>
              </div>
              <div className="seal-item">
                <FaBoxOpen className="seal-icon" />
                <span>Safe for Soil & Roots</span>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM: Tabs / Extended Information */}
        <div className="details-tabs-container">
          <div className="details-tabs-header">
            <button className="tab-title active">Product Specifications & Benefits</button>
          </div>

          <div className="details-tab-content">
            {/* Description rendering HTML */}
            {description && (
              <div className="description-html-content">
                <h2>Overview & Usage Guidelines</h2>
                <div dangerouslySetInnerHTML={{ __html: description.replace(/\n/g, '<br/>') }} />
              </div>
            )}

            {/* Specifications attributes table */}
            <div className="specifications-table-wrap">
              <h2>Product Details</h2>
              <table className="specs-table">
                <tbody>
                  <tr>
                    <td><strong>Category</strong></td>
                    <td>{category}</td>
                  </tr>
                  <tr>
                    <td><strong>Nature</strong></td>
                    <td>100% Fermented Organic Formulation</td>
                  </tr>
                  {product.sku && (
                    <tr>
                      <td><strong>Product SKU</strong></td>
                      <td>{product.sku}</td>
                    </tr>
                  )}
                  {selectedVariant && (
                    <tr>
                      <td><strong>Package Net Vol</strong></td>
                      <td>{selectedVariant.size_label || selectedVariant.sku}</td>
                    </tr>
                  )}
                  <tr>
                    <td><strong>Standard Dilution Rate</strong></td>
                    <td>3 - 5 ml per Litre of water (Foliar spray)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Customer reviews display */}
            <div className="reviews-section-display">
              <h2>Recent Feedback ({staticReviews.length})</h2>
              <div className="reviews-list-grid">
                {staticReviews.map(r => (
                  <div key={r.id} className="review-card">
                    <div className="review-meta">
                      <strong>{r.name}</strong>
                      <span className="review-date">{r.date}</span>
                    </div>
                    <div className="review-rating-stars">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <FaStar key={i} className={i < r.rating ? 'star filled' : 'star'} />
                      ))}
                    </div>
                    <p className="review-comment-text">{r.comment}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}
